import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { VideoCoordinatorService } from '@src/app/core/services/video-coordinator.service';

declare global {
  interface Window {
    YT: any;
    onYouTubeIframeAPIReady: any;
  }
}

let uniqueId = 0;

@Component({
  selector: 'app-video-player',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './video-player.component.html',
  styleUrl: './video-player.component.scss',
  // Full (default) players sit under a section title, so add a gap above them.
  // The compact customThumbnail variant is used in grids/modals with no title
  // above it, so it opts out of the gap.
  host: {
    '[class.title-video-gap]': '!customThumbnail',
  },
})
export class VideoPlayerComponent implements OnInit, AfterViewInit, OnDestroy {
  constructor(
    private sanitizer: DomSanitizer,
    private videoCoordinator: VideoCoordinatorService
  ) { }

  @Input() videoSrc =
    'https://www.youtube.com/embed/hHFKSNKfhvw?modestbranding=1&amp;enablejsapi=1&amp;origin=https://www.mywowhealth.com';
  @Input() title = 'WoW HealthSharing | Save 50% on your healthcare bills | WoW Health';
  @Input() customThumbnail = false;
  @Input() heading: any = '';
  @Input() description: any = '';

  @ViewChild('videoFrame') videoFrame!: ElementRef<HTMLIFrameElement>;
  @ViewChild('nativeVideo') nativeVideo!: ElementRef<HTMLVideoElement>;

  isYouTube = true;
  videoUrl!: SafeResourceUrl;
  fileUrl = '';
  frameId = `wow-video-${uniqueId++}`;
  private player: any;

  ngOnInit(): void {
    const normalised = this.videoSrc.replace(/&amp;/g, '&');
    this.isYouTube = /youtube\.com|youtu\.be/.test(normalised);

    if (this.isYouTube) {
      this.videoUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.buildSrc(normalised));
    } else {
      // Direct media file (e.g. .mp4) — rendered via a native <video> element.
      this.fileUrl = normalised;
    }
  }

  private buildSrc(normalised: string): string {
    try {
      const url = new URL(normalised);
      // enablejsapi=1 is required for the JS API to control playback.
      url.searchParams.set('enablejsapi', '1');
      // origin MUST match the page origin or postMessage (events + pauseVideo) is blocked.
      url.searchParams.set('origin', window.location.origin);
      return url.toString();
    } catch {
      // Fallback for non-absolute URLs — just make sure enablejsapi is present.
      return normalised.includes('enablejsapi=1')
        ? normalised
        : normalised + (normalised.includes('?') ? '&' : '?') + 'enablejsapi=1';
    }
  }

  async ngAfterViewInit(): Promise<void> {
    if (!this.isYouTube) {
      // Native <video>: hook into the coordinator so only one plays at a time.
      const el = this.nativeVideo?.nativeElement;
      if (el) {
        this.player = { pauseVideo: () => el.pause() };
        el.addEventListener('play', () => this.videoCoordinator.setActive(this.player));
      }
      return;
    }

    await this.videoCoordinator.loadYouTubeApi();
    this.player = new window.YT.Player(this.frameId, {
      events: {
        onStateChange: (event: any) => {
          // 1 === YT.PlayerState.PLAYING
          if (event.data === window.YT.PlayerState.PLAYING) {
            this.videoCoordinator.setActive(this.player);
          }
        },
      },
    });
  }

  /**
   * Pause playback. Called when the containing modal/popup is closed so the
   * video does not keep playing in the background. Safe to call before the
   * player is ready or after it has been destroyed.
   */
  pause(): void {
    this.videoCoordinator.clear(this.player);
    if (this.player && typeof this.player.pauseVideo === 'function') {
      try {
        this.player.pauseVideo();
      } catch {
        // Player wasn't ready or was already destroyed — ignore.
      }
    }
  }

  ngOnDestroy(): void {
    this.videoCoordinator.clear(this.player);
    if (this.player && typeof this.player.destroy === 'function') {
      this.player.destroy();
    }
  }
}
